import { useEffect, useMemo, useRef, useState } from 'react'

import { CircularProgress, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { interfaces } from 'service/interfaces'

const origenToQuery = (origen) => {
  const normalized = Number(origen)
  if (normalized === 1) return 'ECO'
  if (normalized === 2) return 'OEM'
  if (normalized === 3) return 'DOS'
  if (typeof origen === 'string' && origen) return origen
  return 'ECO'
}

const mapClientOption = (c) => {
  const id = c?.id || c?.clienteId || c?.cliente_id || c?.codigo || c?.value
  const baseName =
    c?.name || c?.nombre || c?.razon_social || c?.denominacion || c?.label || c?.descripcion || ''
  const cuit = c?.cuit ? ` - ${c.cuit}` : ''
  const status = c?.status || c?.estado || ''
  const statusText = status ? ` (${status})` : ''
  return { ...c, id, label: `${baseName}${cuit}${statusText}`.trim() }
}

export default function ClientSearchAutocomplete({
  origen, // 1|2|3
  valueId, // clienteId actual (number|string)
  onSelectId, // (id) => void
  disabled,
  label = 'Cliente',
  minChars = 3,
  limit = 20,
  error = false,
  helperText = '',
}) {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  const debounceRef = useRef(null)
  const lastKeyRef = useRef('')

  // option seleccionada (para mostrarla si ya hay id)
  const selectedOption = useMemo(() => {
    if (!valueId) return null
    return (
      options.find((o) => String(o.id) === String(valueId)) ||
      (inputValue ? { id: valueId, label: inputValue } : null)
    )
  }, [valueId, options, inputValue])

  // cuando cambia origen, resetea
  useEffect(() => {
    setInputValue('')
    setOptions([])
    onSelectId?.('')
    lastKeyRef.current = ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origen])

  useEffect(() => {
    const term = String(inputValue || '').trim()
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (term.length < minChars) {
      setOptions([])
      lastKeyRef.current = ''
      setLoading(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      const origenQuery = origenToQuery(origen)
      const requestKey = `${origenQuery}::${term}`
      if (lastKeyRef.current === requestKey) return
      lastKeyRef.current = requestKey

      setLoading(true)
      try {
        const query = `?origen=${encodeURIComponent(origenQuery)}&q=${encodeURIComponent(
          term
        )}&limit=${limit}`

        const res = await interfaces.salesClients.searchAutocompleteClients(query)

        setOptions(res?.data?.map(mapClientOption).filter((x) => x?.id && x?.label))
      } finally {
        setLoading(false)
      }
    }, 1000)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [inputValue, origen, minChars, limit])

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason !== 'input') return
        setInputValue(newInput)
        // invalida selección si empieza a escribir
        if (valueId) onSelectId?.('')
      }}
      onChange={(_, opt, reason) => {
        if (reason === 'clear') {
          if (disabled) return
          onSelectId?.('')
          setInputValue('')
          return
        }
        if (reason !== 'select-option') return
        onSelectId?.(opt?.id || '')
        setInputValue(opt?.label || '')
      }}
      getOptionLabel={(o) => o?.label || ''}
      noOptionsText={
        inputValue.trim().length < minChars
          ? `Escribí al menos ${minChars} caracteres`
          : 'Sin resultados'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Buscá por nombre o CUIT"
          fullWidth
          label={label}
          error={Boolean(error)}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
